/**
 * Line geometry, built on the CPU into one interleaved buffer.
 *
 * Everything in the room view is lines. That is partly a drawing decision -
 * a wireframe reads as a technical diagram rather than a video game, which is
 * the right register for a measurement - and partly a practical one: WebGPU
 * has no line width, so every line is exactly one device pixel. On a Swiss
 * grid that is not a limitation, it is the hairline rule.
 */

export interface LineBatch {
  /** Interleaved position (3) + colour (4), two vertices per segment. */
  data: Float32Array;
  vertexCount: number;
}

export type Rgba = [number, number, number, number];

export class LineBuilder {
  private readonly values: number[] = [];

  segment(ax: number, ay: number, az: number, bx: number, by: number, bz: number, colour: Rgba): void {
    this.values.push(ax, ay, az, ...colour, bx, by, bz, ...colour);
  }

  /** Axis-aligned box centred on the origin. */
  box(halfX: number, halfY: number, halfZ: number, colour: Rgba): void {
    const xs = [-halfX, halfX];
    const ys = [-halfY, halfY];
    const zs = [-halfZ, halfZ];
    for (const y of ys) {
      for (const z of zs) this.segment(xs[0], y, z, xs[1], y, z, colour);
    }
    for (const x of xs) {
      for (const z of zs) this.segment(x, ys[0], z, x, ys[1], z, colour);
    }
    for (const x of xs) {
      for (const y of ys) this.segment(x, y, zs[0], x, y, zs[1], colour);
    }
  }

  /**
   * Latitude and longitude wireframe sphere.
   *
   * The shape that says "known distance, unknown direction" without needing a
   * caption: every point on it is equally consistent with the measurement.
   */
  sphere(
    radius: number,
    colour: Rgba,
    options: { rings?: number; meridians?: number; segments?: number; centreY?: number } = {},
  ): void {
    const rings = options.rings ?? 6;
    const meridians = options.meridians ?? 12;
    const segments = options.segments ?? 64;
    const centreY = options.centreY ?? 0;

    // Latitude circles, excluding the poles where they degenerate to points.
    for (let r = 1; r < rings; r++) {
      const phi = (Math.PI * r) / rings;
      const y = Math.cos(phi) * radius + centreY;
      const ringRadius = Math.sin(phi) * radius;
      this.circle(ringRadius, 'y', y, colour, segments);
    }

    // Longitude half-circles.
    for (let m = 0; m < meridians; m++) {
      const theta = (2 * Math.PI * m) / meridians;
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      let previous: [number, number, number] | null = null;
      for (let s = 0; s <= segments; s++) {
        const phi = (Math.PI * s) / segments;
        const ringRadius = Math.sin(phi) * radius;
        const point: [number, number, number] = [
          ringRadius * cos,
          Math.cos(phi) * radius + centreY,
          ringRadius * sin,
        ];
        if (previous) this.segment(...previous, ...point, colour);
        previous = point;
      }
    }
  }

  /** Circle in the plane normal to `axis`, offset along that axis. */
  circle(radius: number, axis: 'x' | 'y' | 'z', offset: number, colour: Rgba, segments = 64): void {
    if (radius <= 1e-6) return;
    let previous: [number, number, number] | null = null;
    for (let i = 0; i <= segments; i++) {
      const angle = (2 * Math.PI * i) / segments;
      const a = Math.cos(angle) * radius;
      const b = Math.sin(angle) * radius;
      const point: [number, number, number] =
        axis === 'y' ? [a, offset, b] : axis === 'x' ? [offset, a, b] : [a, b, offset];
      if (previous) this.segment(...previous, ...point, colour);
      previous = point;
    }
  }

  /** Metric floor grid, one line per `step` metres. */
  grid(extent: number, step: number, colour: Rgba, y = 0): void {
    const count = Math.floor(extent / step);
    for (let i = -count; i <= count; i++) {
      const p = i * step;
      this.segment(p, y, -extent, p, y, extent, colour);
      this.segment(-extent, y, p, extent, y, p, colour);
    }
  }

  /** Three-dimensional cross, for marking the phone at the origin. */
  cross(size: number, colour: Rgba): void {
    this.segment(-size, 0, 0, size, 0, 0, colour);
    this.segment(0, -size, 0, 0, size, 0, colour);
    this.segment(0, 0, -size, 0, 0, size, colour);
  }

  /** Open or closed polyline through a list of points. */
  polyline(points: [number, number, number][], colour: Rgba, close = false): void {
    for (let i = 1; i < points.length; i++) {
      this.segment(...points[i - 1], ...points[i], colour);
    }
    if (close && points.length > 2) {
      this.segment(...points[points.length - 1], ...points[0], colour);
    }
  }

  /**
   * Dashed polyline, dashes measured in metres along the path.
   *
   * Reserved for the parts of an outline that were never measured. A floor
   * plan drawn in one weight implies the whole thing was surveyed, and in a
   * room where the far wall never stood clear of the reverberation that is a
   * lie told in the most persuasive medium available. Dashes are how drawings
   * have always said "assumed".
   */
  dashedPolyline(points: [number, number, number][], colour: Rgba, dashM = 0.12, gapM = 0.1): void {
    let carry = 0;
    let drawing = true;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const dz = b[2] - a[2];
      const length = Math.hypot(dx, dy, dz);
      if (length < 1e-9) continue;
      let t = 0;
      while (t < length) {
        const span = drawing ? dashM - carry : gapM - carry;
        const end = Math.min(length, t + span);
        if (drawing) {
          this.segment(
            a[0] + (dx * t) / length, a[1] + (dy * t) / length, a[2] + (dz * t) / length,
            a[0] + (dx * end) / length, a[1] + (dy * end) / length, a[2] + (dz * end) / length,
            colour,
          );
        }
        if (end - t >= span - 1e-9) {
          drawing = !drawing;
          carry = 0;
        } else {
          carry += end - t;
        }
        t = end;
      }
    }
  }

  /** Box with a yaw about the vertical axis, for furniture that is not square to the room. */
  orientedBox(
    centre: [number, number, number],
    half: [number, number, number],
    yawRad: number,
    colour: Rgba,
  ): void {
    const c = Math.cos(yawRad);
    const s = Math.sin(yawRad);
    const corner = (u: number, v: number, w: number): [number, number, number] => [
      centre[0] + u * half[0] * c - w * half[2] * s,
      centre[1] + v * half[1],
      centre[2] + u * half[0] * s + w * half[2] * c,
    ];
    for (const v of [-1, 1]) {
      this.polyline(
        [corner(-1, v, -1), corner(1, v, -1), corner(1, v, 1), corner(-1, v, 1)],
        colour,
        true,
      );
    }
    for (const u of [-1, 1]) {
      for (const w of [-1, 1]) this.segment(...corner(u, -1, w), ...corner(u, 1, w), colour);
    }
  }

  /** Vertical panel from `a` to `b` along the floor, rising to `topY`. */
  wallPanel(
    a: [number, number],
    b: [number, number],
    floorY: number,
    topY: number,
    colour: Rgba,
    rungs = 3,
  ): void {
    this.segment(a[0], floorY, a[1], b[0], floorY, b[1], colour);
    this.segment(a[0], topY, a[1], b[0], topY, b[1], colour);
    this.segment(a[0], floorY, a[1], a[0], topY, a[1], colour);
    this.segment(b[0], floorY, b[1], b[0], topY, b[1], colour);
    for (let i = 1; i < rungs; i++) {
      const y = floorY + ((topY - floorY) * i) / rungs;
      this.segment(a[0], y, a[1], b[0], y, b[1], colour);
    }
  }

  /** A small three-armed tick, for one cell of the volume. */
  tick(x: number, y: number, z: number, size: number, colour: Rgba): void {
    this.segment(x - size, y, z, x + size, y, z, colour);
    this.segment(x, y - size, z, x, y + size, z, colour);
    this.segment(x, y, z - size, x, y, z + size, colour);
  }

  build(): LineBatch {
    const data = new Float32Array(this.values);
    return { data, vertexCount: data.length / 7 };
  }
}

/** Unit sphere used for the animated wavefront, scaled in the vertex shader. */
export function unitSphere(colour: Rgba): LineBatch {
  const builder = new LineBuilder();
  builder.sphere(1, colour, { rings: 5, meridians: 10, segments: 48 });
  return builder.build();
}
